import * as THREE from 'three';
import { loadGlb } from '../assets/loadGlb';

/** Standing height, meters. Origin is the soles. */
export const PLAYER_VISUAL_HEIGHT = 1.78;
const ROWAN_GLB = '/models/chr/chr_rowan.glb';

const HIP_Y = 0.95;
const TORSO_H = 0.54;
const HEAD_R = 0.12;
const UPPER_LEG = 0.43;
const LOWER_LEG = 0.44;
const FOOT_H = 0.08;
const UPPER_ARM = 0.28;
const LOWER_ARM = 0.26;
const SPEAR_LEN = 2.1;
const WALK_SPEED_MIN = 0.4;

const SKIN = 0xb08a68;
const PANTS = 0x3f4032;
const BOOT = 0x2c261e;
const WOOD = 0x6b5340;
const STONE = 0x8a8680;

export type PlayerPose = {
  sneak: boolean;
  speed: number;
  yaw: number;
  grounded: boolean;
};

/**
 * Primitive humanoid, swapped for chr_rowan.glb when the file loads.
 * Group origin is at the feet; socket_rhand is the spear attach.
 */
export class PlayerVisual extends THREE.Group {
  socket_rhand: THREE.Object3D;

  private readonly pelvis: THREE.Group;
  private readonly leftThigh: THREE.Group;
  private readonly rightThigh: THREE.Group;
  private readonly leftShin: THREE.Group;
  private readonly rightShin: THREE.Group;
  private readonly leftUpper: THREE.Group;
  private readonly rightUpper: THREE.Group;
  private readonly leftLower: THREE.Group;
  private readonly rightLower: THREE.Group;
  private held: THREE.Object3D | null = null;
  private heldId: string | null = null;
  private punchUntil = 0;
  private mixer: THREE.AnimationMixer | null = null;
  private lastMix = 0;
  private idleAct: THREE.AnimationAction | null = null;
  private walkAct: THREE.AnimationAction | null = null;
  private sneakAct: THREE.AnimationAction | null = null;
  private attackAct: THREE.AnimationAction | null = null;
  private currentAct: THREE.AnimationAction | null = null;
  private hero: THREE.Group | null = null;
  private loadedClips: string[] = [];
  private readonly heldRestPos = new THREE.Vector3();
  private readonly heldRestRot = new THREE.Euler();
  private readonly swipe: THREE.Group;
  private readonly swipeLight: THREE.PointLight;
  private readonly sparks: THREE.Mesh[] = [];

  constructor() {
    super();
    this.name = 'PlayerVisual';

    const skin = mat(SKIN, 0.72);
    const pants = mat(PANTS, 0.88);
    const boot = mat(BOOT, 0.9);
    const hide = hideMat();

    this.pelvis = new THREE.Group();
    this.pelvis.name = 'pelvis';
    this.pelvis.position.y = HIP_Y;
    this.add(this.pelvis);

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.38, TORSO_H, 0.22), hide);
    torso.name = 'torso';
    torso.position.y = TORSO_H * 0.5;
    this.pelvis.add(torso);

    const head = new THREE.Mesh(new THREE.SphereGeometry(HEAD_R, 10, 8), skin);
    head.name = 'head';
    head.position.y = TORSO_H + 0.05 + HEAD_R;
    this.pelvis.add(head);

    const leftArm = arm(this.pelvis, -1, skin);
    const rightArm = arm(this.pelvis, 1, skin);
    this.leftUpper = leftArm.upper;
    this.rightUpper = rightArm.upper;
    this.leftLower = leftArm.lower;
    this.rightLower = rightArm.lower;

    this.socket_rhand = new THREE.Object3D();
    this.socket_rhand.name = 'socket_rhand';
    this.socket_rhand.position.set(0, -LOWER_ARM, 0);
    this.rightLower.add(this.socket_rhand);

    this.swipe = new THREE.Group();
    this.swipe.name = 'attack_swipe';
    this.swipe.visible = false;
    const streakMat = new THREE.MeshBasicMaterial({
      color: 0xfff1c4,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    for (const [x, y, z, len, rot] of [
      [0.18, 1.22, -0.55, 1.35, 1.15],
      [0.08, 1.08, -0.72, 1.05, 0.85],
      [0.28, 1.32, -0.4, 0.85, 1.35],
    ] as const) {
      const streak = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.004, len, 5), streakMat.clone());
      streak.position.set(x, y, z);
      streak.rotation.x = rot;
      this.swipe.add(streak);
    }
    this.swipeLight = new THREE.PointLight(0xffe08a, 0, 4.2, 1.6);
    this.swipeLight.position.set(0.2, 1.25, -0.9);
    this.swipe.add(this.swipeLight);
    for (let i = 0; i < 7; i++) {
      const spark = new THREE.Mesh(
        new THREE.SphereGeometry(0.018, 5, 4),
        new THREE.MeshBasicMaterial({ color: 0xfff6d0, transparent: true, opacity: 0, depthWrite: false }),
      );
      spark.position.set(0.12 + i * 0.04, 1.1 + (i % 3) * 0.06, -0.45 - i * 0.08);
      this.swipe.add(spark);
      this.sparks.push(spark);
    }
    this.add(this.swipe);

    const leftLeg = leg(this.pelvis, -1, pants, boot);
    const rightLeg = leg(this.pelvis, 1, pants, boot);
    this.leftThigh = leftLeg.thigh;
    this.rightThigh = rightLeg.thigh;
    this.leftShin = leftLeg.shin;
    this.rightShin = rightLeg.shin;
    this.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = false;
      }
    });
    void this.tryLoadRowan();
  }

  setPose(pose: PlayerPose): void {
    this.rotation.y = pose.yaw;
    this.tickMixer();
    if (this.hero) {
      this.blendClips(pose);
    } else {
      this.posePrimitive(pose);
    }
    this.applyAttack();
  }

  clipNames(): string[] {
    return this.loadedClips.slice();
  }

  activeClip(): string | null {
    return this.currentAct?.getClip().name ?? null;
  }

  /** Short right-arm / spear thrust. Spear.tryAttack and Axe.tryAttack call this. */
  punch(): void {
    this.punchUntil = performance.now() + 700;
    if (this.attackAct) {
      this.attackAct.reset();
      this.attackAct.setLoop(THREE.LoopOnce, 1);
      this.attackAct.clampWhenFinished = true;
      this.attackAct.setEffectiveWeight(1);
      this.attackAct.fadeIn(0.04);
      this.attackAct.play();
      if (this.currentAct && this.currentAct !== this.attackAct) {
        this.currentAct.fadeOut(0.08);
      }
      this.currentAct = this.attackAct;
    }
  }

  isAttacking(): boolean {
    return this.punchUntil > performance.now();
  }

  setHeld(id: string | null): void {
    if (id === this.heldId) {
      return;
    }
    this.heldId = id;
    if (this.held) {
      this.socket_rhand.remove(this.held);
      this.held = null;
    }
    const made = makeHeld(id);
    if (made) {
      this.held = made;
      this.socket_rhand.add(this.held);
      this.captureHeldRest();
      if (id === 'spear') {
        void this.tryLoadSpear();
      } else if (id === 'axe') {
        void this.tryLoadAxe();
      }
    }
  }

  private posePrimitive(pose: PlayerPose): void {
    const sneak = pose.sneak;
    const walking = pose.grounded && pose.speed > WALK_SPEED_MIN;
    const t = performance.now() * 0.001;
    const phase = walking ? t * 8.2 : 0;
    const swing = walking ? Math.sin(phase) * (sneak ? 0.28 : 0.48) : 0;
    const bob = walking ? Math.sin(phase * 2) * 0.03 : 0;

    const hipDrop = sneak ? 0.47 : 0;
    this.pelvis.position.y = HIP_Y - hipDrop + bob;

    const thighSneak = sneak ? 1.2 : 0.06;
    const shinSneak = sneak ? -1.95 : 0.04;
    this.leftThigh.rotation.x = thighSneak + swing;
    this.rightThigh.rotation.x = thighSneak - swing;
    this.leftShin.rotation.x = shinSneak + Math.max(0, -swing) * 0.55;
    this.rightShin.rotation.x = shinSneak + Math.max(0, swing) * 0.55;

    const armBase = sneak ? 0.55 : 0.18;
    this.leftUpper.rotation.x = armBase - swing * 0.7;
    this.rightUpper.rotation.x = armBase + swing * 0.7;
    this.leftLower.rotation.x = sneak ? -0.35 : -0.18;
    this.rightLower.rotation.x = sneak ? -0.28 : -0.12;
  }

  private applyAttack(): void {
    const remain = this.punchUntil - performance.now();
    const active = remain > 0;
    const t = active ? 1 - remain / 700 : 0;
    const thrust = active ? Math.sin(t * Math.PI) : 0;

    if (!this.hero && active) {
      this.rightUpper.rotation.x -= 1.15 * thrust;
      this.rightLower.rotation.x -= 0.35 * thrust;
    }

    if (this.held) {
      this.held.position.copy(this.heldRestPos);
      this.held.rotation.copy(this.heldRestRot);
      if (active) {
        this.held.position.z -= 0.72 * thrust;
        this.held.position.y += 0.1 * thrust;
        this.held.rotation.x -= 0.95 * thrust;
      }
    }

    this.swipe.visible = active;
    const fade = active ? Math.sin(t * Math.PI) : 0;
    this.swipe.position.z = -0.15 - 0.55 * thrust;
    this.swipeLight.intensity = fade * 2.4;
    this.swipe.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      const mat = obj.material;
      if (mat instanceof THREE.MeshBasicMaterial) {
        mat.opacity = fade * (obj.geometry.type === 'SphereGeometry' ? 0.95 : 0.72);
      }
    });
    for (let i = 0; i < this.sparks.length; i++) {
      const spark = this.sparks[i];
      if (!spark) {
        continue;
      }
      const u = (i + 1) / this.sparks.length;
      spark.position.set(0.1 + u * 0.18, 1.05 + thrust * 0.2 + (i % 2) * 0.08, -0.35 - thrust * 0.95 - u * 0.35);
    }
  }

  private captureHeldRest(): void {
    if (!this.held) {
      return;
    }
    this.heldRestPos.copy(this.held.position);
    this.heldRestRot.copy(this.held.rotation);
  }

  private tickMixer(): void {
    if (!this.mixer) {
      return;
    }
    const now = performance.now() * 0.001;
    const dt = this.lastMix > 0 ? now - this.lastMix : 0;
    this.lastMix = now;
    this.mixer.update(Math.max(0, dt));
  }

  /** Hide the box humanoid when Rowan GLB is present. Keep primitives on failure. */
  private async tryLoadRowan(): Promise<void> {
    let hero: THREE.Group;
    try {
      hero = await loadGlb(ROWAN_GLB);
    } catch {
      return;
    }
    hero.name = 'chr_rowan';
    hero.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = false;
      }
    });
    const box = new THREE.Box3().setFromObject(hero);
    const size = box.getSize(new THREE.Vector3());
    // Y-up GLB already stands ~1.78 m. Only lift a leftover Z-up file.
    const alreadyUp = size.y >= 1.2;
    if (!alreadyUp && size.z > size.y * 1.35 && size.z > 1.2) {
      hero.rotation.x = -Math.PI / 2;
      box.setFromObject(hero);
    }
    if (Number.isFinite(box.min.y)) {
      hero.position.y -= box.min.y;
    }
    const socket = hero.getObjectByName('socket_rhand');
    this.add(hero);
    this.hero = hero;
    hero.visible = true;
    this.pelvis.visible = false;
    if (socket) {
      if (this.held) {
        this.socket_rhand.remove(this.held);
        socket.add(this.held);
      }
      this.socket_rhand.name = 'socket_rhand_prim';
      this.socket_rhand = socket;
    } else {
      this.socket_rhand.removeFromParent();
      this.add(this.socket_rhand);
    }
    const clips = clipsOf(hero);
    this.loadedClips = clips.map((clip) => clip.name).filter(Boolean);
    const idleClip =
      THREE.AnimationClip.findByName(clips, 'ply_idle') ??
      THREE.AnimationClip.findByName(clips, 'idle') ??
      clips[0];
    const walkClip =
      THREE.AnimationClip.findByName(clips, 'ply_walk') ??
      THREE.AnimationClip.findByName(clips, 'walk') ??
      THREE.AnimationClip.findByName(clips, 'ply_run') ??
      idleClip;
    const sneakClip =
      THREE.AnimationClip.findByName(clips, 'ply_sneak') ??
      THREE.AnimationClip.findByName(clips, 'sneak') ??
      walkClip;
    const attackClip =
      THREE.AnimationClip.findByName(clips, 'ply_attack') ??
      THREE.AnimationClip.findByName(clips, 'attack') ??
      THREE.AnimationClip.findByName(clips, 'ply_thrust') ??
      null;
    if (idleClip) {
      this.mixer = new THREE.AnimationMixer(hero);
      this.idleAct = this.mixer.clipAction(idleClip);
      this.walkAct = walkClip ? this.mixer.clipAction(walkClip) : this.idleAct;
      this.sneakAct = sneakClip ? this.mixer.clipAction(sneakClip) : this.walkAct;
      this.attackAct = attackClip ? this.mixer.clipAction(attackClip) : null;
      for (const act of [this.idleAct, this.walkAct, this.sneakAct]) {
        act.enabled = true;
        act.setLoop(THREE.LoopRepeat, Infinity);
        act.clampWhenFinished = false;
        act.setEffectiveWeight(0);
      }
      if (this.attackAct) {
        this.attackAct.enabled = true;
        this.attackAct.setLoop(THREE.LoopOnce, 1);
        this.attackAct.clampWhenFinished = true;
        this.attackAct.setEffectiveWeight(0);
      }
      this.idleAct.setEffectiveWeight(1);
      this.idleAct.play();
      this.currentAct = this.idleAct;
    }
  }

  private blendClips(pose: PlayerPose): void {
    if (!this.idleAct || !this.walkAct || !this.sneakAct) {
      return;
    }
    if (this.isAttacking() && this.attackAct && this.currentAct === this.attackAct) {
      return;
    }
    const walking = pose.grounded && pose.speed > WALK_SPEED_MIN;
    const sneak = pose.sneak && walking;
    const want = sneak ? this.sneakAct : walking ? this.walkAct : this.idleAct;
    if (want && want !== this.currentAct) {
      want.reset();
      want.setEffectiveWeight(1);
      want.fadeIn(0.16);
      want.play();
      this.currentAct?.fadeOut(0.16);
      this.currentAct = want;
    }
    if (this.walkAct && this.currentAct === this.walkAct) {
      this.walkAct.setEffectiveTimeScale(pose.speed > 4.5 ? 1.35 : 1);
    }
  }

  private async tryLoadSpear(): Promise<void> {
    if (this.heldId !== 'spear') {
      return;
    }
    try {
      const hero = await loadGlb('/models/wpn/wpn_spear.glb');
      if (this.heldId !== 'spear') {
        return;
      }
      if (this.held) {
        this.socket_rhand.remove(this.held);
      }
      this.held = hero;
      this.socket_rhand.add(hero);
      this.captureHeldRest();
    } catch {
      /* keep primitive spear */
    }
  }

  private async tryLoadAxe(): Promise<void> {
    if (this.heldId !== 'axe') {
      return;
    }
    try {
      const hero = await loadGlb('/models/tool/tool_axe_hatchet.glb');
      if (this.heldId !== 'axe') {
        return;
      }
      hero.name = 'held_axe';
      if (this.held) {
        this.socket_rhand.remove(this.held);
      }
      this.held = hero;
      this.socket_rhand.add(hero);
      this.captureHeldRest();
    } catch {
      /* keep primitive hatchet */
    }
  }
}

function clipsOf(root: THREE.Object3D): THREE.AnimationClip[] {
  const raw = root.userData.animations;
  return Array.isArray(raw) ? (raw as THREE.AnimationClip[]) : [];
}

function mat(color: number, roughness: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.04 });
}

function hideMat(): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: 0x6a6a50,
    roughness: 0.86,
    metalness: 0.03,
  });
  const texture = new THREE.TextureLoader().load('/tex/hide.jpg');
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1.2, 1.6);
  material.map = texture;
  return material;
}

function arm(
  pelvis: THREE.Group,
  side: -1 | 1,
  skin: THREE.Material,
): { upper: THREE.Group; lower: THREE.Group } {
  const upper = new THREE.Group();
  upper.name = side > 0 ? 'arm_r' : 'arm_l';
  upper.position.set(side * 0.22, TORSO_H - 0.04, 0);
  const upperMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.055, UPPER_ARM, 7), skin);
  upperMesh.position.y = -UPPER_ARM * 0.5;
  const lower = new THREE.Group();
  lower.name = side > 0 ? 'forearm_r' : 'forearm_l';
  lower.position.y = -UPPER_ARM;
  const lowerMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.046, LOWER_ARM, 7), skin);
  lowerMesh.position.y = -LOWER_ARM * 0.5;
  lower.add(lowerMesh);
  upper.add(upperMesh, lower);
  pelvis.add(upper);
  return { upper, lower };
}

function leg(
  pelvis: THREE.Group,
  side: -1 | 1,
  pants: THREE.Material,
  boot: THREE.Material,
): { thigh: THREE.Group; shin: THREE.Group } {
  const thigh = new THREE.Group();
  thigh.name = side > 0 ? 'thigh_r' : 'thigh_l';
  thigh.position.set(side * 0.1, 0, 0);
  const thighMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.072, UPPER_LEG, 7), pants);
  thighMesh.position.y = -UPPER_LEG * 0.5;
  const shin = new THREE.Group();
  shin.name = side > 0 ? 'shin_r' : 'shin_l';
  shin.position.y = -UPPER_LEG;
  const shinMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.058, LOWER_LEG, 7), pants);
  shinMesh.position.y = -LOWER_LEG * 0.5;
  const foot = new THREE.Mesh(new THREE.BoxGeometry(0.11, FOOT_H, 0.24), boot);
  foot.name = side > 0 ? 'foot_r' : 'foot_l';
  foot.position.set(0, -LOWER_LEG - FOOT_H * 0.5, -0.04);
  shin.add(shinMesh, foot);
  thigh.add(thighMesh, shin);
  pelvis.add(thigh);
  return { thigh, shin };
}

function makeHeld(id: string | null): THREE.Group | null {
  switch (id) {
    case 'spear':
      return makeSpear();
    case 'bow':
      return makeBow();
    case 'torch':
      return makeTorch();
    case 'shovel':
      return makeShovel();
    case 'rope':
      return makeRope();
    case 'tracker':
      return makeTracker();
    case 'axe':
      return makeAxe(false);
    case 'metal_axe':
      return makeAxe(true);
    case 'sling':
      return makeSling();
    case 'compass':
      return makeCompass();
    default:
      return null;
  }
}

/** 2.1 m wreck-shaft + groundstone tip. Grip sits in socket_rhand. */
function makeSpear(): THREE.Group {
  const spear = new THREE.Group();
  spear.name = 'held_spear';
  const shaftLen = SPEAR_LEN - 0.16;
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.018, 0.024, shaftLen, 6),
    mat(WOOD, 0.78),
  );
  shaft.position.y = 0.38;
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(0.038, 0.16, 5),
    new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.45, metalness: 0.22 }),
  );
  tip.position.y = 0.38 + shaftLen * 0.5 + 0.08;
  spear.add(shaft, tip);
  spear.rotation.set(-0.55, 0, -0.18);
  return spear;
}

/** Recurved stave + fiber string. Grip sits in socket_rhand. */
function makeBow(): THREE.Group {
  const bow = new THREE.Group();
  bow.name = 'held_bow';
  const upper = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.55, 0.05), mat(WOOD, 0.78));
  upper.position.set(0, 0.42, 0.04);
  upper.rotation.z = 0.28;
  const lower = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.48, 0.05), mat(WOOD, 0.78));
  lower.position.set(0, -0.08, 0.04);
  lower.rotation.z = -0.22;
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.14, 0.04), mat(WOOD, 0.7));
  grip.position.set(0, 0.16, 0.02);
  const string = new THREE.Mesh(
    new THREE.CylinderGeometry(0.006, 0.006, 0.92, 5),
    new THREE.MeshStandardMaterial({ color: 0xc8b48a, roughness: 0.55, metalness: 0.02 }),
  );
  string.position.set(0.12, 0.18, 0.02);
  bow.add(upper, lower, grip, string);
  bow.rotation.set(-0.35, 0.4, -0.55);
  return bow;
}

function makeTorch(): THREE.Group {
  const torch = new THREE.Group();
  torch.name = 'held_torch';
  const stick = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.022, 0.55, 6), mat(WOOD, 0.8));
  stick.position.y = 0.22;
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.045, 7, 6),
    new THREE.MeshStandardMaterial({
      color: 0xff8a2a,
      emissive: 0xff6a12,
      emissiveIntensity: 1.4,
      roughness: 0.45,
      metalness: 0.04,
    }),
  );
  head.position.y = 0.52;
  torch.add(stick, head);
  torch.rotation.set(-0.4, 0, -0.12);
  return torch;
}

function makeShovel(): THREE.Group {
  const shovel = new THREE.Group();
  shovel.name = 'held_shovel';
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.02, 0.7, 6), mat(WOOD, 0.8));
  shaft.position.y = 0.28;
  const blade = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.18, 0.03),
    new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.5, metalness: 0.28 }),
  );
  blade.position.y = 0.68;
  shovel.add(shaft, blade);
  shovel.rotation.set(-0.5, 0, -0.16);
  return shovel;
}

function makeRope(): THREE.Group {
  const rope = new THREE.Group();
  rope.name = 'held_rope';
  const coil = new THREE.Mesh(
    new THREE.TorusGeometry(0.08, 0.022, 6, 10),
    new THREE.MeshStandardMaterial({ color: 0x8a6a40, roughness: 0.85, metalness: 0.04 }),
  );
  coil.rotation.x = 0.7;
  coil.position.y = 0.12;
  rope.add(coil);
  return rope;
}

function makeTracker(): THREE.Group {
  const tracker = new THREE.Group();
  tracker.name = 'held_tracker';
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.12, 0.04),
    new THREE.MeshStandardMaterial({ color: 0x3a4038, roughness: 0.45, metalness: 0.22 }),
  );
  body.position.y = 0.1;
  const lamp = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.02, 0.01),
    new THREE.MeshStandardMaterial({
      color: 0x6adf7a,
      emissive: 0x2a8a3a,
      emissiveIntensity: 0.8,
      roughness: 0.3,
      metalness: 0.1,
    }),
  );
  lamp.position.set(0, 0.14, 0.022);
  tracker.add(body, lamp);
  return tracker;
}

function makeAxe(metal: boolean): THREE.Group {
  const axe = new THREE.Group();
  axe.name = metal ? 'held_metal_axe' : 'held_axe';
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.02, 0.62, 6), mat(WOOD, 0.78));
  shaft.position.y = 0.24;
  const blade = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 0.1, 0.03),
    new THREE.MeshStandardMaterial({
      color: metal ? 0x8a9096 : 0x7a7670,
      roughness: metal ? 0.32 : 0.55,
      metalness: metal ? 0.55 : 0.18,
    }),
  );
  blade.position.set(0.06, 0.52, 0);
  axe.add(shaft, blade);
  axe.rotation.set(-0.48, 0.1, -0.2);
  return axe;
}

function makeSling(): THREE.Group {
  const sling = new THREE.Group();
  sling.name = 'held_sling';
  const fork = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, 0.02), mat(WOOD, 0.75));
  fork.position.y = 0.16;
  const band = new THREE.Mesh(
    new THREE.BoxGeometry(0.09, 0.02, 0.01),
    new THREE.MeshStandardMaterial({ color: 0xc8b48a, roughness: 0.6, metalness: 0.02 }),
  );
  band.position.y = 0.24;
  sling.add(fork, band);
  sling.rotation.set(-0.3, 0.2, -0.15);
  return sling;
}

function makeCompass(): THREE.Group {
  const compass = new THREE.Group();
  compass.name = 'held_compass';
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055, 0.055, 0.016, 12),
    new THREE.MeshStandardMaterial({ color: 0x6a5a38, roughness: 0.4, metalness: 0.35 }),
  );
  disc.position.y = 0.08;
  compass.add(disc);
  return compass;
}
